import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Download } from 'lucide-react';
import { api } from '../../lib/api';
import InvoiceStatusChip from '../../components/crm/InvoiceStatusChip';
import SignaturePanel from '../../components/portal/SignaturePanel';
import InvoiceTemplateFrame from '../../components/financial/InvoiceTemplateFrame';
import PaymentSchedulePanel from '../../components/financial/PaymentSchedulePanel';
import { useAuth } from '../../context/AuthContext';

// Portal Invoice detail — Sprint D item 3.2.
// Renders invoice content + signature panel (when required and not signed).
// The full Sprint E invoice template replaces the inline rendering below;
// this version is a simple readable summary so the signature flow can ship.

function formatMoney(cents, currency = 'USD') {
  if (cents == null) return '—';
  return (Number(cents) / 100).toLocaleString('en-US', { style: 'currency', currency });
}

function formatDate(s) { return s ? s.slice(0, 10) : '—'; }

export default function PortalInvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = React.useCallback(() => {
    setLoading(true);
    return api.get(`/api/client/invoices/${id}`)
      .then(setData)
      .catch((err) => setError(err?.data?.error || err?.message || 'Failed to load invoice'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-[13px] text-gray-500">Loading…</div>;
  if (error) return <div className="text-[13px] text-red-300/90">{error}</div>;
  if (!data) return null;

  const { invoice, line_items: lineItems, signature, payment_schedule: schedule, account } = data;
  const needsSignature = invoice.signature_required === 1 && !invoice.signed_at;

  const handleSign = async (payload) => {
    setSubmitting(true);
    try {
      await api.post(`/api/client/invoices/${invoice.id}/sign`, payload);
      load();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="portal-invoice-detail" className="space-y-6">
      <button
        type="button"
        onClick={() => navigate('/portal/invoices')}
        className="inline-flex items-center gap-1.5 text-[12px] text-gray-400 hover:text-white"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back to invoices
      </button>

      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-gray-400">Invoice</div>
            <div className="text-2xl font-semibold text-white mt-1" data-testid="invoice-number">
              {invoice.invoice_number || invoice.id.slice(0, 8)}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={`/api/invoices/${invoice.id}/pdf`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="invoice-export-pdf"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] bg-white/[0.04] hover:bg-white/[0.08] text-white border border-white/[0.08]"
            >
              <Download className="w-3.5 h-3.5" /> Export PDF
            </a>
            <InvoiceStatusChip invoice={invoice} audience="client" />
          </div>
        </div>

        <dl className="grid grid-cols-2 gap-y-3 gap-x-6 text-[13px]">
          <DescItem label="Issued"        value={formatDate(invoice.issued_at)} />
          <DescItem label="Due"           value={formatDate(invoice.due_at)} />
          <DescItem label="Valid through" value={formatDate(invoice.valid_through)} />
          <DescItem label="PO #"          value={invoice.po_number || '—'} />
          <DescItem label="Tax"           value={invoice.tax_exempt === 1 ? 'EXEMPT' : (invoice.tax_amount_cents ? formatMoney(invoice.tax_amount_cents) : '—')} />
          <DescItem label="Amount"        value={formatMoney(invoice.amount_cents, invoice.currency)} bold />
        </dl>

        {lineItems?.length > 0 && (
          <table className="w-full mt-6 text-[12px]" data-testid="invoice-line-items">
            <thead className="text-gray-500 uppercase tracking-wider text-[10px]">
              <tr><th className="text-left py-1.5">Description</th><th className="text-right">Qty</th><th className="text-right">Unit</th><th className="text-right">Amount</th></tr>
            </thead>
            <tbody className="divide-y divide-white/[0.04]">
              {lineItems.map((li) => (
                <tr key={li.id}>
                  <td className="py-2 text-gray-300">{li.description}</td>
                  <td className="py-2 text-right text-gray-400">{li.qty}</td>
                  <td className="py-2 text-right text-gray-400">{formatMoney(li.unit_cost_cents)}</td>
                  <td className="py-2 text-right text-white">{formatMoney(li.amount_cents ?? (li.qty ?? 0) * (li.unit_cost_cents ?? 0))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {invoice.notes_client && (
          <div className="mt-6 px-3 py-2.5 rounded-lg bg-white/[0.02] border border-white/[0.06] text-[12px] text-gray-300">
            {invoice.notes_client}
          </div>
        )}
      </div>

      <InvoiceTemplateFrame
        invoice={invoice}
        lineItems={lineItems}
        account={account}
        signature={signature}
      />

      {schedule?.length > 0 && (
        <PaymentSchedulePanel schedule={schedule} currency={invoice.currency} />
      )}

      {invoice.signed_at && signature && (
        <div data-testid="signed-stamp" className="rounded-2xl border border-[#B0FC38]/30 bg-[#B0FC38]/[0.05] p-5 flex items-center gap-3">
          <CheckCircle2 className="w-6 h-6 text-[#B0FC38]" />
          <div className="text-[13px] text-gray-200">
            Signed by <span className="font-semibold text-white">{user?.name || 'you'}</span> on{' '}
            <span className="font-medium">{formatDate(signature.signed_at)}</span>
          </div>
        </div>
      )}

      {needsSignature && (
        <SignaturePanel
          defaultName={user?.name || ''}
          submitting={submitting}
          onSubmit={handleSign}
        />
      )}

      {schedule?.length > 0 && (
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5">
          <div className="text-[11px] uppercase tracking-wider text-gray-400 mb-2">Payment schedule</div>
          <ul className="space-y-1.5 text-[13px]">
            {schedule.map((s) => (
              <li key={s.id} className="flex items-center justify-between">
                <span className="text-gray-300">{formatDate(s.expected_date)} · {s.notes || 'Scheduled payment'}</span>
                <span className="text-white">{formatMoney(s.expected_amount_cents)}{s.fulfilled_payment_id ? ' (paid)' : ''}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function DescItem({ label, value, bold }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-wider text-gray-500">{label}</dt>
      <dd className={`mt-0.5 ${bold ? 'text-white font-semibold' : 'text-gray-300'}`}>{value}</dd>
    </div>
  );
}
